export type MatchType = 'league' | 'playoff';

// Legacy type - kept for database compatibility but not used in Volleyball scoring
export type LostReason = 'ACE' | 'IN' | 'OUT' | 'TOUCH' | 'NET' | 'FOUL';

export type MatchStatus = 'scheduled' | 'setup' | 'first-server' | 'live' | 'set-won' | 'match-won' | 'timeout';

export type TossChoice = 'serve' | 'side';

// Volleyball categories for point winner assignment
export type VolleyballCategory = 'A' | 'D' | 'L' | 'S' | 'B' | 'F';

export interface VolleyballCategoryLabels {
  A: string;
  D: string;
  L: string;
  S: string;
  B: string;
  F: string;
}

export const VOLLEYBALL_CATEGORY_LABELS: VolleyballCategoryLabels = {
  A: 'ACER',
  D: 'DEFENDER',
  L: 'LIBERO',
  S: 'SETTER',
  B: 'BLOCKER',
  F: 'FINISHER',
};

// Category assignment per rally
export interface CategoryAssignment {
  playerId: string;
  category: VolleyballCategory;
}

export interface TournamentRules {
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
}

export interface Tournament {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  venue: string | null;
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
  createdAt: string;
  updatedAt: string;
}

export interface Player {
  id: string;
  name: string;
  number: number;
  teamId: string;
}

export interface Team {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players: Player[];
}

export interface Match {
  id: string;
  matchNumber: number;
  date: string;
  matchTime: string | null;
  matchType: MatchType;
  teamAId: string;
  teamBId: string;
  tossWonById: string | null;
  tossChoice: TossChoice | null;
  status: MatchStatus;
  winnerId: string | null;
  tournamentId: string | null;
  winningPoint: number | null;
  goldenPoint: number | null;
  startTime: string | null;
  endTime: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SetRecord {
  id: string;
  matchId: string;
  setNumber: number;
  scoreA: number;
  scoreB: number;
  winnerId: string | null;
  startedAt: string;
  endedAt: string | null;
  rallies?: Rally[];
  sideChangeShown?: boolean;
}

export interface Timeout {
  id: string;
  matchId: string;
  setNumber: number;
  teamId: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
}

export interface Rally {
  id: string;
  setId: string;
  rallyNumber: number;
  servingTeamId: string;
  serverId: string;
  pointWonById: string;
  lostReason: LostReason | null;
  pointWinnerId: string | null;
  recordedAt: string;
  // Volleyball category assignments
  categories?: CategoryAssignment[];
  // Server fault flag
  isServerFault?: boolean;
}

export interface RotationState {
  serveHistory: string[];
  cycleServed: string[];
}

export interface TeamState {
  id: string;
  name: string;
  shortName: string;
  color: string;
  logoUrl?: string | null;
  players: Player[];
  setsWon: number;
  rotationState: RotationState;
}

export interface SetState {
  scoreA: number;
  scoreB: number;
  rallies: Rally[];
  servingTeamId: string;
  currentServerId: string;
  goldenPoint: boolean;
}

export interface MatchState {
  match: Match | null;
  teamA: TeamState | null;
  teamB: TeamState | null;
  currentSetNumber: number;
  currentSet: SetState | null;
  status: MatchStatus;
  sets: SetRecord[];
  tournamentRules?: TournamentRules | null;
  serveRotationCooloff?: number; // number of other players who must serve before a player can serve again (default 5)
}

export interface ValidationError {
  id: string;
  title: string;
  message: string;
}

export interface FirstServerSelection {
  teamId: string;
  teamName: string;
  players: Player[];
}

export interface ServerSelectionData {
  teamId: string;
  teamName: string;
  players: Player[];
  eligiblePlayerIds: string[];
  reason: 'first-server' | 'service-break';
}

export interface PointWinnerSelectionData {
  teamId: string;
  teamName: string;
  players: Player[];
  lostReason: LostReason;
}

export const MAX_SCORE = 21;
export const WIN_SCORE = 15;
export const WIN_DIFFERENCE = 2;
export const MIN_ROTATION_GAP = 6; // Volleyball: 5 other players must serve before repeat

export const DEFAULT_TOURNAMENT_RULES: TournamentRules = {
  leagueWinningPoint: 15,
  leagueGoldenPoint: 21,
  playoffWinningPoint: 21,
  playoffGoldenPoint: 31,
};

export const MATCH_CONFIG: Record<MatchType, { maxSets: number; winsNeeded: number }> = {
  league: { maxSets: 3, winsNeeded: 2 },
  playoff: { maxSets: 5, winsNeeded: 3 },
};

export interface MatchRulesConfig {
  winningPoint: number;
  goldenPoint: number;
  maxSets: number;
  winsNeeded: number;
}

export function getMatchRules(matchType: MatchType, rules: TournamentRules | null, matchWinningPoint?: number | null, matchGoldenPoint?: number | null): MatchRulesConfig {
  const matchConfig = MATCH_CONFIG[matchType];

  // If match has its own rules (from creation time), use those
  if (matchWinningPoint !== null && matchWinningPoint !== undefined && matchGoldenPoint !== null && matchGoldenPoint !== undefined) {
    return {
      winningPoint: matchWinningPoint,
      goldenPoint: matchGoldenPoint,
      maxSets: matchConfig.maxSets,
      winsNeeded: matchConfig.winsNeeded,
    };
  }

  // If tournament rules exist, use those
  if (rules) {
    return matchType === 'league'
      ? {
          winningPoint: rules.leagueWinningPoint,
          goldenPoint: rules.leagueGoldenPoint,
          maxSets: matchConfig.maxSets,
          winsNeeded: matchConfig.winsNeeded,
        }
      : {
          winningPoint: rules.playoffWinningPoint,
          goldenPoint: rules.playoffGoldenPoint,
          maxSets: matchConfig.maxSets,
          winsNeeded: matchConfig.winsNeeded,
        };
  }

  // Fallback to defaults
  return matchType === 'league'
    ? {
        winningPoint: DEFAULT_TOURNAMENT_RULES.leagueWinningPoint,
        goldenPoint: DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint,
        maxSets: matchConfig.maxSets,
        winsNeeded: matchConfig.winsNeeded,
      }
    : {
        winningPoint: DEFAULT_TOURNAMENT_RULES.playoffWinningPoint,
        goldenPoint: DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint,
        maxSets: matchConfig.maxSets,
        winsNeeded: matchConfig.winsNeeded,
      };
}

// Legacy constants - kept for backward compatibility but not used in Volleyball scoring
export const LOST_REASONS: LostReason[] = ['ACE', 'IN', 'OUT', 'TOUCH', 'NET', 'FOUL'];

export const LOST_REASON_REQUIRES_WINNER: Record<LostReason, boolean> = {
  ACE: false,
  IN: true,
  OUT: false,
  TOUCH: true,
  NET: false,
  FOUL: false,
};

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  ACE: 'ACE',
  IN: 'IN',
  OUT: 'OUT',
  TOUCH: 'TOUCH',
  NET: 'NET',
  FOUL: 'FOUL',
};
